import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, Spin, message, Typography, Row, Col, Card, Radio } from 'antd';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import Header from "../../components/Header/Header";
import SideNavbar from "../../components/Navbar/SideNavbar";

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface OtpDataPoint {
    count: number;
    timestamp: number;
}

const OtpAnalyticsDashboard: React.FC = () => {
    const [quickFilter, setQuickFilter] = useState<string>('today');
    // Default to today (1 day)
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<OtpDataPoint[]>([]);

    const fetchAnalytics = async (start: Dayjs, end: Dayjs) => {
        setLoading(true);
        try {
            const diffInSeconds = end.diff(start, 'second');
            let bucketDuration = 60; // default 1 hour in minutes

            if (diffInSeconds <= 3600) {
                bucketDuration = 1; // 1 min
            } else if (diffInSeconds <= 86400) {
                bucketDuration = 60; // 1 hour
            } else if (diffInSeconds <= 3 * 86400) {
                bucketDuration = 240; // 4 hours
            } else if (diffInSeconds <= 7 * 86400) {
                bucketDuration = 360; // 6 hours
            } else {
                bucketDuration = 1440; // 1 day
            }

            const response = await axiosInstance.get(apis.FETCH_OTP_ANALYTICS, {
                params: {
                    startDate: start.toISOString(),
                    endDate: end.toISOString(),
                    bucketDuration: bucketDuration
                }
            });

            if (response?.data?.status && response?.data?.data) {
                setData(response.data.data);
            } else {
                setData([]);
                message.error('Failed to load data.');
            }
        } catch (error) {
            console.error('Error fetching OTP analytics:', error);
            message.error('Error fetching analytics data');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            fetchAnalytics(dateRange[0], dateRange[1]);
        }
    }, [dateRange]);

    const handleDateChange = (dates: any) => {
        setQuickFilter('');
        if (dates && dates.length === 2) {
            // Cap at 90 days maximum
            const diffDays = dates[1].diff(dates[0], 'day');
            if (diffDays > 90) {
                message.warning('Date range cannot exceed 90 days. Truncating to 90 days.');
                setDateRange([dates[0].startOf('day'), dates[0].add(90, 'day').endOf('day')]);
            } else {
                setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
            }
        }
    };

    const handleQuickFilter = (e: any) => {
        const filter = e.target.value;
        setQuickFilter(filter);
        const end = dayjs().endOf('day');
        let start = dayjs().startOf('day');

        if (filter === 'today') {
            start = dayjs().startOf('day');
        } else if (filter === 'yesterday') {
            start = dayjs().subtract(1, 'day').startOf('day');
            setDateRange([start, dayjs().subtract(1, 'day').endOf('day')]);
            return;
        } else if (filter === '3days') {
            start = dayjs().subtract(2, 'day').startOf('day');
        } else if (filter === '7days') {
            start = dayjs().subtract(6, 'day').startOf('day');
        } else if (filter === '30days') {
            start = dayjs().subtract(29, 'day').startOf('day');
        } else if (filter === '90days') {
            start = dayjs().subtract(89, 'day').startOf('day');
        }

        setDateRange([start, end]);
    };

    const formattedData = useMemo(() => {
        return data.map(item => {
            const date = moment(item.timestamp).utcOffset('+05:30');
            const diffInDays = dateRange[1].diff(dateRange[0], 'day');

            // Format intelligently based on selected range
            let formatStr = 'MMM DD, HH:mm';
            if (diffInDays <= 1) {
                formatStr = 'HH:mm';
            } else if (diffInDays > 7) {
                formatStr = 'MMM DD';
            }

            return {
                ...item,
                timeLabel: date.format(formatStr),
                fullDate: date.format('YYYY-MM-DD HH:mm:ss')
            };
        });
    }, [data, dateRange]);

    const totalOtpSent = useMemo(() => {
        return data.reduce((acc, curr) => acc + curr.count, 0);
    }, [data]);

    return (
        <React.Fragment>
            <div className="flex mainWrapper w-full">
                <div className="sidenav-wrapper"><SideNavbar /></div>
                <div className="right-container">
                    <div className="header-container"><Header /></div>
                    <div className="body-container">
                        <div style={{ padding: '20px 24px', background: '#f0f2f5', minHeight: '100vh', fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex flex-wrap gap-4 items-center justify-between">
                                <div>
                                    <Title level={4} className="!m-0">OTP Sent Over Time</Title>
                                    <p className="text-gray-500 text-sm mt-1">Track the volume of OTPs sent. Max filter 90 days.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Radio.Group value={quickFilter} onChange={handleQuickFilter} optionType="button" buttonStyle="solid">
                                        <Radio.Button value="today">Today</Radio.Button>
                                        <Radio.Button value="yesterday">Yesterday</Radio.Button>
                                        <Radio.Button value="3days">Last 3 Days</Radio.Button>
                                        <Radio.Button value="7days">7 Days</Radio.Button>
                                        <Radio.Button value="30days">30 Days</Radio.Button>
                                        <Radio.Button value="90days">90 Days</Radio.Button>
                                    </Radio.Group>
                                    <RangePicker
                                        value={dateRange as any}
                                        onChange={handleDateChange}
                                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                                        className="w-[350px]"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm">
                                    <Spin size="large" tip="Loading Analytics..." />
                                </div>
                            ) : (
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <Card className="shadow-sm">
                                            <div className="mb-4">
                                                <p className="text-gray-500 text-sm font-medium">Total OTPs Sent (Selected Range)</p>
                                                <Title level={2} className="!m-0 !text-blue-600">{totalOtpSent.toLocaleString()}</Title>
                                            </div>
                                            <div style={{ width: '100%', height: 400 }}>
                                                <ResponsiveContainer>
                                                    <LineChart
                                                        data={formattedData}
                                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                                        <XAxis
                                                            dataKey="timeLabel"
                                                            stroke="#6B7280"
                                                            tick={{ fill: '#6B7280', fontSize: 12 }}
                                                            tickLine={false}
                                                            axisLine={{ stroke: '#E5E7EB' }}
                                                            minTickGap={30}
                                                        />
                                                        <YAxis
                                                            stroke="#6B7280"
                                                            tick={{ fill: '#6B7280', fontSize: 12 }}
                                                            tickLine={false}
                                                            axisLine={false}
                                                        />
                                                        <RechartsTooltip
                                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                                            labelStyle={{ fontWeight: 'bold', color: '#374151', marginBottom: '4px' }}
                                                            labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDate}
                                                            formatter={(value: any) => [value, 'OTPs Sent']}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="count"
                                                            stroke="#2563EB"
                                                            strokeWidth={3}
                                                            dot={false}
                                                            activeDot={{ r: 6, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                                                            animationDuration={1000}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                                {formattedData.length === 0 && (
                                                    <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                                                        <p className="text-gray-500">No data available for the selected range</p>
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default OtpAnalyticsDashboard;
